<template>
  <a-modal
    :open="visible"
    :title="qrData?.name || 'QR코드 미리보기'"
    :width="500"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="modal-content" v-if="qrData">
      <div class="qr-preview-section">
        <canvas v-if="qrUrl" id="full-qr-preview"></canvas>
        <span v-else>QR코드를 표시할 수 없습니다.</span>
      </div>

      <div class="qr-info-section">
        <div class="info-row">
          <span class="label">QR코드명:</span>
          <span class="value">{{ qrData.name }}</span>
        </div>
        <div class="info-row" v-if="qrData.description">
          <span class="label">설명:</span>
          <span class="value">{{ qrData.description }}</span>
        </div>
        <div class="info-row" v-if="qrData.trackingEnabled !== false">
          <span class="label">Short Code:</span>
          <code>{{ qrData.shortCode }}</code>
        </div>
        <div class="info-row">
          <span class="label">{{ qrUrlLabel }}:</span>
          <code class="qr-url">{{ qrUrl || 'URL을 확인할 수 없습니다.' }}</code>
          <a-tooltip :title="`${qrUrlLabel} 복사`">
            <a-button
              type="link"
              size="small"
              :aria-label="`${qrUrlLabel} 복사`"
              :disabled="!qrUrl"
              @click="copyQrUrl"
            >
              <template #icon><copy-outlined /></template>
            </a-button>
          </a-tooltip>
        </div>
        <div class="info-row" v-if="qrData.trackingEnabled !== false">
          <span class="label">목적지 URL:</span>
          <a :href="qrData.targetUrl" target="_blank" rel="noopener noreferrer" class="target-url">
            {{ qrData.targetUrl }}
          </a>
        </div>
        <div class="info-row">
          <span class="label">통계 수집:</span>
          <span class="value">
            <a-tag v-if="qrData.trackingEnabled !== false" color="blue">수집 중</a-tag>
            <a-tag v-else color="default">미수집 (직접 URL)</a-tag>
          </span>
        </div>
      </div>

      <!-- 직접 URL QR코드 안내 -->
      <div v-if="qrData.trackingEnabled === false" class="direct-url-notice">
        <i class="fa-solid fa-circle-info"></i>
        <span>이 QR코드는 목적지 URL로 직접 연결됩니다. 스캔 통계가 수집되지 않으며, URL 변경이 불가능합니다.</span>
      </div>

      <div class="download-section">
        <a-button :disabled="!qrUrl" @click="downloadQr('png')">
          <template #icon><download-outlined /></template>
          PNG 다운로드
        </a-button>
        <a-button :disabled="!qrUrl" @click="downloadQr('svg')">
          <template #icon><download-outlined /></template>
          SVG 다운로드
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { defineComponent, watch, nextTick, computed } from 'vue';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import QRCode from 'qrcode';
import { resolveQrContentUrl } from '../utils/qrUrls';

export default defineComponent({
  name: 'QrPreviewModal',
  components: {
    CopyOutlined,
    DownloadOutlined
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    qrData: {
      type: Object,
      default: null
    },
    baseUrl: {
      type: String,
      default: ''
    }
  },
  emits: ['update:visible'],
  setup(props, { emit }) {
    const qrUrl = computed(() => resolveQrContentUrl(
      props.baseUrl || window.location.origin,
      props.qrData
    ));
    const qrUrlLabel = computed(() => props.qrData?.trackingEnabled === false
      ? '직접 연결 URL'
      : '단축 URL');

    const generateQr = async () => {
      await nextTick();
      const canvas = document.getElementById('full-qr-preview');
      if (canvas && qrUrl.value) {
        try {
          await QRCode.toCanvas(canvas, qrUrl.value, {
            width: 250,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#ffffff'
            }
          });
        } catch (err) {
          console.error('QR 생성 오류:', err);
        }
      }
    };

    watch(() => props.visible, (newVal) => {
      if (newVal && props.qrData) {
        generateQr();
      }
    });

    const handleCancel = () => {
      emit('update:visible', false);
    };

    const copyQrUrl = async () => {
      const urlToCopy = qrUrl.value;
      const urlLabel = qrUrlLabel.value;
      if (!urlToCopy) return;
      try {
        if (!navigator.clipboard?.writeText) {
          message.error('이 브라우저에서는 자동 복사를 사용할 수 없습니다. URL을 선택해 복사해주세요.');
          return;
        }
        await navigator.clipboard.writeText(urlToCopy);
        message.success(`${urlLabel}이 복사되었습니다.`);
      } catch {
        message.error('복사에 실패했습니다. URL을 선택해 복사해주세요.');
      }
    };

    const downloadQr = async (format) => {
      if (!qrUrl.value) return;

      const filename = `qr-${props.qrData.shortCode}`;

      if (format === 'png') {
        const canvas = document.getElementById('full-qr-preview');
        if (canvas) {
          const link = document.createElement('a');
          link.download = `${filename}.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
          message.success('PNG 파일이 다운로드되었습니다.');
        }
      } else if (format === 'svg') {
        try {
          const svgString = await QRCode.toString(qrUrl.value, {
            type: 'svg',
            width: 250,
            margin: 2
          });

          const blob = new Blob([svgString], { type: 'image/svg+xml' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = `${filename}.svg`;
          link.href = url;
          link.click();
          URL.revokeObjectURL(url);
          message.success('SVG 파일이 다운로드되었습니다.');
        } catch (err) {
          console.error('SVG 생성 오류:', err);
          message.error('SVG 다운로드에 실패했습니다.');
        }
      }
    };

    return {
      qrUrl,
      qrUrlLabel,
      handleCancel,
      copyQrUrl,
      downloadQr
    };
  }
});
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';

.modal-content {
  text-align: center;
}

.qr-preview-section {
  padding: $spacing-lg;
  background-color: $bg-light;
  border-radius: $border-radius-md;
  margin-bottom: $spacing-md;

  canvas {
    border: 1px solid $border-color;
    border-radius: $border-radius-sm;
    background-color: $bg-white;
  }
}

.qr-info-section {
  text-align: left;
  padding: $spacing-md;
  background-color: $bg-light;
  border-radius: $border-radius-md;
  margin-bottom: $spacing-md;

  .info-row {
    display: flex;
    align-items: flex-start;
    margin-bottom: $spacing-sm;

    &:last-child {
      margin-bottom: 0;
    }

    .label {
      width: 100px;
      flex-shrink: 0;
      color: $text-muted;
      font-size: $font-size-sm;
    }

    .value {
      flex: 1;
      font-weight: $font-weight-medium;
    }

    code {
      background-color: $bg-white;
      padding: 2px 6px;
      border-radius: $border-radius-sm;
      font-size: $font-size-sm;
      max-width: 300px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &.qr-url {
        flex: 1;
        min-width: 0;
        max-width: none;
        overflow-wrap: anywhere;
        white-space: normal;
      }
    }

    .target-url {
      color: $primary-color;
      text-decoration: none;
      word-break: break-all;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.download-section {
  display: flex;
  justify-content: center;
  gap: $spacing-md;
}

.direct-url-notice {
  display: flex;
  align-items: flex-start;
  gap: $spacing-sm;
  padding: $spacing-sm $spacing-md;
  background-color: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: $border-radius-sm;
  margin-bottom: $spacing-md;
  font-size: $font-size-sm;
  color: $text-muted;
  text-align: left;

  i {
    color: #fa8c16;
    margin-top: 2px;
    flex-shrink: 0;
  }
}
</style>
